// ============================================================
// src/components/permissions/LocationRolePermissionMatrix.js
// Updated on: 2025-11-04  –  Final version
// Adds: Default tenant=1 (Super Admin), gradient tenant chip,
// Sync Permissions button.
// ============================================================

import React, { useEffect, useState, useContext } from "react";
import Select from "react-select";
import { Spinner } from "react-bootstrap";
import {
  getActiveTenants,
  getRolesByTenant,
  getLocationsByTenant,
  getModules,
  getLocationRolePermissions,
  updateLocationRolePermissions,
  cloneTenantToLocationPermissions,
} from "../../services/locationPermissionService";
import { AuthContext } from "../../context/AuthContext";
import "../../styles/UserForm.css";
import { MdArrowDropUp, MdArrowDropDown } from "react-icons/md";


export default function LocationRolePermissionMatrix() {
  const { user } = useContext(AuthContext);
  const roleCode = (user?.role_code || "").toUpperCase();
  const userTenantId = user?.tenant_id || null;

  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [modules, setModules] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sortKey, setSortKey] = useState("module");
  const [sortDir, setSortDir] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // ==========================================================
  // 🎨 Gradient Select Styling
  // ==========================================================
  const selectStyles = {
    container: (base) => ({ ...base, minWidth: 220, zIndex: 10 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base, state) => ({
      ...base,
      background:
        selectedTenant && !state.isMulti
          ? "linear-gradient(90deg, rgba(0,123,255,0.9) 0%, rgba(111,66,193,0.9) 100%)"
          : base.background,
      color: selectedTenant && !state.isMulti ? "white" : base.color,
      borderColor: state.isFocused ? "#007bff" : base.borderColor,
      boxShadow: state.isFocused ? "0 0 0 1px #007bff" : base.boxShadow,
    }),
    singleValue: (base) => ({
      ...base,
      color: selectedTenant ? "white" : base.color,
      fontWeight: 500,
    }),
    multiValue: (base) => ({
      ...base,
      background:
        "linear-gradient(90deg, rgba(0,123,255,0.9) 0%, rgba(111,66,193,0.9) 100%)",
      borderRadius: "12px",
      color: "white",
      boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      fontWeight: "500",
      padding: "0 6px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": { backgroundColor: "rgba(255,255,255,0.2)", color: "white" },
    }),
  };

  // ==========================================================
  // 🔹 Load Tenants (Super Admin or Tenant Admin)
  // ==========================================================
  useEffect(() => {
    const loadTenants = async () => {
      try {
        if (roleCode === "SUPER_ADMIN") {
          const data = await getActiveTenants();
          const tenantOptions = (data || []).map((t) => ({
            value: t.id,
            label: t.name,
          }));
          setTenants(tenantOptions);

          // ✅ Always auto-select tenant ID = 1 if exists
          const defaultTenant =
            tenantOptions.find((t) => t.value === 1) || tenantOptions[0] || null;
          if (defaultTenant) setSelectedTenant(defaultTenant);
        } else if (userTenantId) {
          const option = {
            value: userTenantId,
            label: user?.tenant_name || "Current Tenant",
          };
          setTenants([option]);
          setSelectedTenant(option);
        }
      } catch (err) {
        console.error("❌ Error loading tenants:", err);
      }
    };
    loadTenants();
  }, [roleCode, userTenantId]);

  // ==========================================================
  // 🔹 Load Roles, Locations, Modules
  // ==========================================================
  useEffect(() => {
    const loadFilters = async () => {
      try {
        if (!selectedTenant) return;
        const tenantId = selectedTenant.value;

        const [rolesData, locData, modulesData] = await Promise.all([
          getRolesByTenant(tenantId),
          getLocationsByTenant(tenantId),
          getModules(),
        ]);

        setRoles((rolesData || []).map((r) => ({ value: r.id, label: r.name })));
        setLocations((locData || []).map((l) => ({ value: l.id, label: l.name })));
        setModules((modulesData || []).map((m) => ({ value: m, label: m })));
      } catch (err) {
        console.error("❌ Error loading filters:", err);
      }
    };
    loadFilters();
  }, [selectedTenant]);

  // ==========================================================
  // 🔹 Load Permissions
  // ==========================================================
  useEffect(() => {
    const loadPermissions = async () => {
      if (
        !selectedTenant ||
        !selectedRoles.length ||
        !selectedLocations.length
      ) {
        setPermissions([]);
        return;
      }
      setLoading(true);
      try {
        const res = await getLocationRolePermissions({
          tenantId: selectedTenant.value,
          roleIds: selectedRoles.map((r) => r.value),
          locationIds: selectedLocations.map((l) => l.value),
          modules: selectedModules.map((m) => m.value),
        });
        setPermissions(res?.data || []);
      } catch (err) {
        console.error("❌ Failed to load permissions:", err);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };
    loadPermissions();
  }, [selectedTenant, selectedRoles, selectedLocations, selectedModules]);

  // ==========================================================
  // 🔹 Toggle, Save & Sync
  // ==========================================================
  const handleToggle = (funcId, field) =>
    setPermissions((p) =>
      p.map((row) =>
        row.functionality_id === funcId ? { ...row, [field]: !row[field] } : row
      )
    );

  const handleSave = async () => {
    if (!selectedRoles.length || !selectedLocations.length) {
      alert("Please select at least one Role and Location.");
      return;
    }
    setSaving(true);
    const payload = permissions.map((p) => ({
      tenant_id: selectedTenant.value,
      role_id: p.role_id,
      location_id: p.location_id,
      functionality_id: p.functionality_id,
      can_view: p.can_view,
      can_create: p.can_create,
      can_edit: p.can_edit,
      can_delete: p.can_delete,
      can_approve: p.can_approve,
    }));
    try {
      const res = await updateLocationRolePermissions(payload);
      alert(res?.success
        ? "✅ Permissions updated successfully!"
        : "❌ Failed to update permissions.");
    } catch (err) {
      console.error("❌ Error updating permissions:", err);
      alert("❌ Error updating permissions.");
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    if (!selectedTenant || !selectedRoles.length || !selectedLocations.length) {
      alert("Please select Tenant, Role and Location to sync.");
      return;
    }
    setSyncing(true);
    try {
      for (const role of selectedRoles) {
        for (const loc of selectedLocations) {
          await cloneTenantToLocationPermissions(
            selectedTenant.value,
            role.value,
            loc.value
          );
        }
      }
      alert("🔁 Tenant permissions synced successfully!");
    } catch (err) {
      console.error("❌ Sync failed:", err);
      alert("❌ Failed to sync permissions.");
    } finally {
      setSyncing(false);
    }
  };

  // ==========================================================
  // 🔹 Sorting / Pagination Helpers
  // ==========================================================
  const safe = (v) => (v == null ? "" : String(v));
  const toggleSort = (k) =>
    k === sortKey
      ? setSortDir((d) => (d === "asc" ? "desc" : "asc"))
      : (setSortKey(k), setSortDir("asc"));

  const sorted = [...permissions].sort((a, b) => {
    const A = safe(a[sortKey]),
      B = safe(b[sortKey]);
    return sortDir === "asc" ? A.localeCompare(B) : B.localeCompare(A);
  });

  const filtered = sorted.filter((p) => {
    const q = search.toLowerCase();
    return (
      safe(p.functionality_name).toLowerCase().includes(q) ||
      safe(p.code).toLowerCase().includes(q) ||
      safe(p.module).toLowerCase().includes(q)
    );
  });

  const total = filtered.length;
  const pages = Math.ceil(total / rowsPerPage);
  const start = (currentPage - 1) * rowsPerPage;
  const rows = filtered.slice(start, start + rowsPerPage);

  // ==========================================================
  // 🧭 UI
  // ==========================================================
  return (
    <div className="container-fluid">
      {/* Header */}
      <div
        className="users-hero shadow-sm d-flex justify-content-between align-items-center px-4 flex-wrap"
        style={{
          background: "linear-gradient(90deg, #007bff, #6f42c1)",
          color: "white",
        }}
      >
        <div>
          <div className="h4 fw-bold mb-1 text-white">
            Location Role Permission Matrix
          </div>
          <div className="text-white-50 small">
            Manage role permissions for specific locations and modules.
          </div>
        </div>

        <div className="d-flex gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              background: "linear-gradient(90deg, #17a2b8, #138496)",
              border: "none",
              padding: "10px 20px",
              borderRadius: "6px",
              fontWeight: "600",
              color: "white",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
          >
            {syncing ? (
              <>
                <Spinner animation="border" size="sm" /> Syncing...
              </>
            ) : (
              "🔁 Sync Permissions"
            )}
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: "linear-gradient(90deg, #28a745, #6cc57b)",
              border: "none",
              padding: "10px 22px",
              borderRadius: "6px",
              fontWeight: "600",
              color: "white",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" /> Updating...
              </>
            ) : (
              "💾 Update Permissions"
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="users-filter-toolbar d-flex flex-wrap align-items-center gap-2 mt-2">
        <Select
          options={tenants}
          value={selectedTenant}
          onChange={setSelectedTenant}
          placeholder="Select Tenant..."
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
        <Select
          isMulti
          options={locations}
          value={selectedLocations}
          onChange={setSelectedLocations}
          placeholder="Select Location(s)..."
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
        <Select
          isMulti
          options={roles}
          value={selectedRoles}
          onChange={setSelectedRoles}
          placeholder="Select Role(s)..."
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
        <Select
          isMulti
          options={modules}
          value={selectedModules}
          onChange={setSelectedModules}
          placeholder="Filter by Module(s)..."
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </div>

      {/* Search + Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2 px-1">
        <div className="d-flex align-items-center gap-2 small text-muted">
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(+e.target.value);
              setCurrentPage(1);
            }}
            className="form-select form-select-sm"
            style={{ width: "80px" }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
          entries per page
        </div>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 250 }}
          placeholder="Search Functionality / Code / Module..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="card shadow-sm mt-2 table-responsive">
        {loading ? (
          <div className="text-center p-4">Loading permissions...</div>
        ) : rows.length === 0 ? (
          <div className="text-center p-4 text-muted">No data available.</div>
        ) : (
          <table className="table table-sm users-table mb-0">
            <thead className="table-light">
              <tr>
                <th onClick={() => toggleSort("role_name")}>Role</th>
                <th onClick={() => toggleSort("location_name")}>Location</th>
                <th onClick={() => toggleSort("functionality_name")}>
                  Functionality
                </th>
                <th onClick={() => toggleSort("module")}>Module</th>
                <th onClick={() => toggleSort("code")}>Code</th>
                {["View", "Create", "Edit", "Delete", "Approve"].map((h) => (
                  <th key={h} className="text-center">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={`${p.role_id}-${p.location_id}-${p.functionality_id}`}>
                  <td>{p.role_name}</td>
                  <td>{p.location_name}</td>
                  <td>{p.functionality_name}</td>
                  <td>{p.module}</td>
                  <td>{p.code}</td>
                  {[
                    "can_view",
                    "can_create",
                    "can_edit",
                    "can_delete",
                    "can_approve",
                  ].map((f) => (
                    <td key={f} className="text-center">
                      <input
                        type="checkbox"
                        checked={p[f]}
                        onChange={() => handleToggle(p.functionality_id, f)}
                        className="permission-checkbox"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && rows.length > 0 && (
        <div className="d-flex justify-content-between align-items-center p-2 flex-wrap">
          <div className="small text-muted">
            Showing {start + 1} to {Math.min(start + rowsPerPage, total)} of{" "}
            {total} entries
          </div>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setCurrentPage(1)}>
                «
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                ‹
              </button>
            </li>
            {[...Array(pages)].map((_, i) => (
              <li
                key={i}
                className={`page-item ${
                  currentPage === i + 1 ? "active" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === pages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                ›
              </button>
            </li>
            <li
              className={`page-item ${
                currentPage === pages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => setCurrentPage(pages)}
              >
                »
              </button>
            </li>
          </ul>
        </div>
      )}

      <style>{`
        input.permission-checkbox:checked { accent-color: #28a745; }
        .pagination .page-link { color: #007bff; cursor: pointer; }
        .pagination .active .page-link { background-color: #007bff; color: white; border-color: #007bff; }
        th { cursor: pointer; user-select: none; }
        th:hover { background-color: #f1f1f1; }
      `}</style>
    </div>
  );
}
